/**
 * Analytics consent and URL hygiene, shared by the GTM and PostHog plugins and
 * the useAnalytics composable.
 *
 * Consent comes from CookieYes, which GTM loads at Consent Initialization. Its
 * documented API is:
 *   - getCkyConsent(): synchronous, only defined once the banner has loaded.
 *     Returns `{ categories: { necessary, functional, analytics, performance,
 *     advertisement }, isUserActionCompleted, activeLaw, ... }` with boolean
 *     category values.
 *   - `cookieyes_banner_load` document event: the banner (and therefore
 *     getCkyConsent) is ready; `event.detail` is getCkyConsent()'s value. It
 *     fires on every page load, including for a returning visitor whose choice
 *     is already stored — the only signal for them, since GTM loads CookieYes
 *     after this plugin has already read "no consent yet". Not
 *     `cookieyes_banner_loaded`: that name doesn't exist (checked in banner.js),
 *     and listening for it left PostHog off for every returning visitor.
 *   - `cookieyes_consent_update` document event: the visitor changed their
 *     choice. `event.detail = { accepted: string[], rejected: string[] }`,
 *     always using the default category names.
 *
 * Until CookieYes says otherwise the answer is "no consent". Global Privacy
 * Control (navigator.globalPrivacyControl) is treated as a refusal that
 * overrides whatever the banner says.
 */

export interface CkyConsent {
    categories?: Partial<Record<'necessary' | 'functional' | 'analytics' | 'performance' | 'advertisement', boolean>>;
    isUserActionCompleted?: boolean;
}

export interface ConsentInputs {
    /** The result of window.getCkyConsent(), or undefined if CookieYes has not loaded. */
    cky?: CkyConsent | null;
    /** navigator.globalPrivacyControl */
    gpc?: boolean;
}

/** Pure: does this consent state allow analytics? */
export function analyticsConsentFrom({ cky, gpc }: ConsentInputs): boolean {
    if (gpc === true) return false;
    return cky?.categories?.analytics === true;
}

/** Pure: analytics consent from a `cookieyes_consent_update` event detail. */
export function analyticsConsentFromUpdate(detail: unknown, gpc?: boolean): boolean {
    if (gpc === true) return false;
    const accepted = (detail as { accepted?: unknown })?.accepted;
    return Array.isArray(accepted) && accepted.includes('analytics');
}

function readCky(): CkyConsent | undefined {
    try {
        const fn = (window as any).getCkyConsent;
        return typeof fn === 'function' ? fn() : undefined;
    } catch {
        return undefined;
    }
}

function readGpc(): boolean {
    return typeof navigator !== 'undefined' && (navigator as any).globalPrivacyControl === true;
}

/** Client only. Current analytics consent; false on the server or before CookieYes loads. */
export function hasAnalyticsConsent(): boolean {
    if (typeof window === 'undefined') return false;
    return analyticsConsentFrom({ cky: readCky(), gpc: readGpc() });
}

/**
 * Client only. Calls `cb(consented)` whenever CookieYes loads or the visitor
 * changes their choice. Returns an unsubscribe function.
 */
/** CookieYes's load event, as dispatched by its banner.js. */
export const CKY_BANNER_LOAD_EVENT = 'cookieyes_banner_load';

export function onAnalyticsConsentChange(cb: (consented: boolean) => void): () => void {
    if (typeof document === 'undefined') return () => {};
    const onLoaded = (e: Event) =>
        cb(hasAnalyticsConsent() || analyticsConsentFrom({ cky: (e as CustomEvent).detail, gpc: readGpc() }));
    const onUpdate = (e: Event) => cb(analyticsConsentFromUpdate((e as CustomEvent).detail, readGpc()));
    document.addEventListener(CKY_BANNER_LOAD_EVENT, onLoaded);
    document.addEventListener('cookieyes_consent_update', onUpdate);
    return () => {
        document.removeEventListener(CKY_BANNER_LOAD_EVENT, onLoaded);
        document.removeEventListener('cookieyes_consent_update', onUpdate);
    };
}

/**
 * Query parameters that carry credentials. The magic-link sign-in page is
 * reached with `?token=`, and anything that reads the address bar (GTM's
 * page_location, PostHog's $current_url / $referrer) would otherwise send it on.
 */
const SENSITIVE_PARAMS = ['token'];

/** Pure: does this URL carry a credential in its query string? */
export function hasSensitiveParams(url: string): boolean {
    try {
        const u = new URL(url, 'http://x.invalid');
        return SENSITIVE_PARAMS.some((p) => u.searchParams.has(p));
    } catch {
        return /[?&]token=/.test(url);
    }
}

/**
 * Pure: the URL with credential parameters removed. Relative input stays
 * relative. Unparseable input that looks like it carries a token collapses to
 * its path so the token cannot survive.
 */
export function stripSensitiveParams(url: string): string {
    if (!hasSensitiveParams(url)) return url;
    try {
        const absolute = /^[a-z][a-z0-9+.-]*:/i.test(url);
        const u = new URL(url, 'http://x.invalid');
        for (const p of SENSITIVE_PARAMS) u.searchParams.delete(p);
        return absolute ? u.toString() : u.pathname + u.search + u.hash;
    } catch {
        return url.split('?')[0] ?? '';
    }
}

/**
 * Pure: strip credential parameters from every string value in an event's
 * properties, $set and $set_once (PostHog's $current_url, $referrer,
 * $initial_current_url, $pathname...). Shallow by design: those are all
 * top-level. Mutates and returns the event, matching PostHog's before_send.
 */
export function sanitizeEventUrls<T extends { properties?: Record<string, any>; $set?: Record<string, any>; $set_once?: Record<string, any> }>(event: T | null): T | null {
    if (!event) return event;
    for (const bag of [event.properties, event.$set, event.$set_once]) {
        if (!bag) continue;
        for (const key of Object.keys(bag)) {
            const value = bag[key];
            if (typeof value === 'string' && hasSensitiveParams(value)) bag[key] = stripSensitiveParams(value);
        }
    }
    return event;
}

/**
 * Pure: should a router navigation count as a new pageview? Only when the path
 * changes — itinerary pages router.replace() their query string on every edit,
 * which is not a new page.
 */
export function isNewPageview(toPath: string, fromPath: string | undefined): boolean {
    return toPath !== fromPath;
}
